import {
  CcmConsentFormValues,
  ConsentFieldConfig,
  PracticeContact,
} from "./ccm-consent-types";

export const CCM_CONSENT_FORM_ID = "ccm-consent-form";
export const CONSENT_DATE_VALUE_FORMAT = "YYYY-MM-DD";
export const PHONE_MASK = "(999) 999-9999";
export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
export const EMAIL_LINK_PREFIX = "mailto:";
export const REQUIRED_MARK = " *";

export const CCM_CONSENT_STYLE = {
  BORDER: "divider",
  HEADER_BG: "primary.light",
  HEADER_TEXT: "primary.main",
  SUB_HEADING: "warning.dark",
  HEADING_WEIGHT: 600,
  SIGNATURE_WIDTH: "368px",
  SIGNATURE_HEIGHT: "100px",
  EXPIRY_DATE_WIDTH: "200px",
} as const;

// Placeholder until the form is wired to the practice info API.
export const CCM_PRACTICE_CONTACT: PracticeContact = {
  name: "Premier MD Healthcare Providers",
  phone: "(818) 760-5061",
  fax: "(818) 760-5071",
  email: "info@premiermd.health",
  address: "450 N Brand Blvd., Ste. 600 Glendale, CA 91203",
};

export const CONSENT_FIELD_TYPE = {
  TEXT: "text",
  PHONE: "phone",
  DATE: "date",
} as const;

export const GENDER = { MALE: "MALE", FEMALE: "FEMALE" } as const;

export const RELEASE_ITEM = {
  HISTORY_AND_PHYSICALS: "HISTORY_AND_PHYSICALS",
  CURRENT_MEDICAL_FINDINGS: "CURRENT_MEDICAL_FINDINGS",
  MEDICATION_LIST: "MEDICATION_LIST",
  LAB_AND_IMAGING_REPORTS: "LAB_AND_IMAGING_REPORTS",
  CHART_NOTES_AND_TREATMENT_PLAN: "CHART_NOTES_AND_TREATMENT_PLAN",
} as const;

export const AUTHORIZATION_EXPIRY = {
  NEVER_EXPIRES: "NEVER_EXPIRES",
  DATE: "DATE",
} as const;

export const VISIT_MODALITY = {
  AUDIO_VIDEO: "AUDIO_VIDEO",
  AUDIO_ONLY: "AUDIO_ONLY",
  OTHER: "OTHER",
} as const;

export const CCM_CONSENT_TEXT = {
  TITLE: "CHRONIC CARE MANAGEMENT (CCM) CONSENT FORM",
  PATIENT_INFORMATION: "PATIENT INFORMATION",
  INSURANCE_INFORMATION: "INSURANCE INFORMATION",
  EMERGENCY_CONTACT: "EMERGENCY CONTACT",
  RELEASE_AUTHORIZATION: "AUTHORIZATION TO RELEASE HEALTH INFORMATION TO:",
  TELEHEALTH_CONSENT: "TELEHEALTH / TELEMEDICINE CONSENT FORM",
  CONSENT_DOCUMENTATION: "CONSENT DOCUMENTATION",
  CONTACT_INFORMATION: "CONTACT INFORMATION",
  AGE: "Age",
  GENDER: "Gender",
  INSURANCE_TYPE: "TYPE OF INSURANCE: MEDICARE PART B",
  RELEASE_FOLLOWING: "Release the following information:",
  AUTHORIZATION_EXPIRES:
    "AUTHORIZATION EXPIRES (Unless Otherwise Noted This Authorization Will Remain in Effect Twelve Months From the Date Signed)",
  NEVER_EXPIRES: "Never Expires",
  DATE: "Date",
  VISIT_MODALITY: "Visit Modality",
  PATIENT_SIGNATURE: "Patient Signature",
  PHONE: "Phone:",
  FAX: "Fax:",
  EMAIL: "Email:",
  ADDRESS: "Address:",
  CONTACT_INTRO:
    "If you have any questions, requests, or complaints, please contact :",
  ACKNOWLEDGEMENT:
    "I acknowledge that I have received or been offered the opportunity to receive the Notice of Privacy Practices.",
  ACK_PATIENT_SIGNATURE: "Patient's Signature",
  ACK_RESPONSIBLE_SIGNATURE: "Responsible Person / Guardian's Signature",
  UNABLE_TO_SIGN:
    "Patients are unable to sign consent due to physical, cognitive, or language limitations. Patient marked “X” in place of signature.",
  WITNESS_SIGNED: "Witness present & signed on behalf of patient.",
  SIGNATURE_CLEAR: "Clear",
  SIGNATURE_REMOVE: "Remove signature",
  ZIP_CODE_ERROR: "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)",
} as const;

export const PATIENT_FIELDS: ConsentFieldConfig[] = [
  { name: "patient.firstName", label: "First Name", required: true, sm: 6 },
  { name: "patient.lastName", label: "Last Name", required: true, sm: 6 },
  {
    name: "patient.dateOfBirth",
    label: "Date of Birth",
    type: CONSENT_FIELD_TYPE.DATE,
    required: true,
    sm: 4,
  },
];

export const PATIENT_DETAIL_FIELDS: ConsentFieldConfig[] = [
  { name: "patient.race", label: "Race", sm: 6 },
  { name: "patient.preferredLanguage", label: "Preferred Language", sm: 6 },
  { name: "patient.address", label: "Address", sm: 12 },
  { name: "patient.city", label: "City", sm: 4 },
  { name: "patient.state", label: "State", sm: 4 },
  { name: "patient.zipCode", label: "ZIP Code", sm: 4 },
  {
    name: "patient.cellphone",
    label: "Cellphone",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 4,
  },
  {
    name: "patient.homePhone",
    label: "Home Phone",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 4,
  },
  {
    name: "patient.altPhone",
    label: "ALT",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 4,
  },
];

export const INSURANCE_FIELDS: ConsentFieldConfig[] = [
  {
    name: "insurance.subscriberId",
    label: "SUBSCRIBER ID: (POLICY NUMBER)",
    sm: 12,
  },
  { name: "insurance.others", label: "Others", sm: 12 },
];

export const EMERGENCY_CONTACT_FIELDS: ConsentFieldConfig[] = [
  { name: "emergencyContact.name", label: "NAME", sm: 12 },
  {
    name: "emergencyContact.cell",
    label: "CELL",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 6,
  },
  {
    name: "emergencyContact.home",
    label: "HOME",
    type: CONSENT_FIELD_TYPE.PHONE,
    sm: 6,
  },
  { name: "emergencyContact.address", label: "ADDRESS", sm: 12 },
  {
    name: "emergencyContact.relationshipToPatient",
    label: "RELATIONSHIP TO PATIENT",
    sm: 12,
  },
];

export const EXPIRY_DATE_FIELD: ConsentFieldConfig = {
  name: "releaseAuthorization.expiryDate",
  label: "Date",
  type: CONSENT_FIELD_TYPE.DATE,
  allowFutureDate: true,
};

export const SIGNATURE_DATE_FIELD: ConsentFieldConfig = {
  name: "consentDocumentation.signatureDate",
  label: "Date",
  type: CONSENT_FIELD_TYPE.DATE,
};

export const OTHER_MODALITY_FIELD: ConsentFieldConfig = {
  name: "telehealth.otherModality",
  label: "Other",
  required: true,
  sm: 6,
};

export const AUDIO_ONLY_REASON_FIELD: ConsentFieldConfig = {
  name: "telehealth.audioOnlyReason",
  label: "Reason audio-only used, if applicable",
  sm: 6,
};

export const ACKNOWLEDGEMENT_FIELDS: ConsentFieldConfig[] = [
  {
    name: "acknowledgement.relationshipToPatient",
    label: "Relationship to Patient",
    sm: 4,
  },
  { name: "acknowledgement.witnessName", label: "Witness' Name", sm: 4 },
  {
    name: "acknowledgement.date",
    label: "Date",
    type: CONSENT_FIELD_TYPE.DATE,
    sm: 4,
  },
];

export const CCM_CONSENT_INITIAL_VALUES: CcmConsentFormValues = {
  patient: {
    firstName: "",
    lastName: "",
    dateOfBirth: null,
    gender: "",
    race: "",
    preferredLanguage: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    cellphone: "",
    homePhone: "",
    altPhone: "",
  },
  insurance: { subscriberId: "", others: "" },
  emergencyContact: {
    name: "",
    cell: "",
    home: "",
    address: "",
    relationshipToPatient: "",
  },
  releaseAuthorization: { releaseItems: [], expiry: "", expiryDate: null },
  telehealth: {
    verbalConsent: false,
    visitModality: "",
    otherModality: "",
    audioOnlyReason: "",
  },
  consentDocumentation: {
    electronicSmsConsent: false,
    verbalConsent: false,
    signatureDate: null,
  },
  acknowledgement: { relationshipToPatient: "", witnessName: "", date: null },
};
